import { computed, reactive, watchEffect } from 'vue';
import type { CustomFieldProps } from '@satellite/components/custom_forms/types';

/**
 * Normalized props interface for internal use in field components
 */
export interface NormalizedCustomFieldProps {
  fieldIcon: string;
  displayLabel: boolean;
  hideDetails: boolean;
  outlined: boolean;
  singleLine: boolean;
  persistentPlaceholder: boolean;
  timezone?: string;
  militaryTimeEnabled?: boolean;
  readOnly: boolean;
  /** Passed to CustomFieldInputBase as :required */
  required: boolean;
}

/**
 * Composable for normalizing and providing default values for custom field props.
 * Returns a reactive object (not a Ref) so template type-checking works when
 * passing to child components (e.g. normalizedProps.readOnly).
 *
 * @param props - Raw props from the component
 * @returns Normalized props with defaults applied
 */
export function useCustomFieldProps(props: CustomFieldProps) {
  const normalizedProps = reactive<NormalizedCustomFieldProps>({
    fieldIcon: props.fieldIcon ?? '',
    displayLabel: props.displayLabel ?? true,
    hideDetails: props.hideDetails ?? false,
    outlined: props.outlined ?? false,
    singleLine: props.singleLine ?? false,
    persistentPlaceholder: props.persistentPlaceholder ?? true,
    timezone: props.timezone,
    militaryTimeEnabled: props.militaryTimeEnabled,
    readOnly: props.readOnly ?? false,
    required: props.required ?? props.field?.required ?? false
  });

  watchEffect(() => {
    normalizedProps.fieldIcon = props.fieldIcon ?? '';
    normalizedProps.displayLabel = props.displayLabel ?? true;
    normalizedProps.hideDetails = props.hideDetails ?? false;
    normalizedProps.outlined = props.outlined ?? false;
    normalizedProps.singleLine = props.singleLine ?? false;
    normalizedProps.persistentPlaceholder = props.persistentPlaceholder ?? true;
    normalizedProps.timezone = props.timezone;
    normalizedProps.militaryTimeEnabled = props.militaryTimeEnabled;
    normalizedProps.readOnly = props.readOnly ?? false;
    normalizedProps.required = props.required ?? props.field?.required ?? false;
  });

  const shouldDisplayLabel = computed(() => {
    return normalizedProps.displayLabel && !!props.field?.label;
  });

  const effectiveRules = computed(() => {
    return props.rules || [];
  });

  return {
    normalizedProps,
    shouldDisplayLabel,
    effectiveRules
  };
}
